import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EndPoints, UserRoles } from '../../common/enums/enums';
import { companiesFilter, currentUserDataFilter } from '../../helpers/users';
import { amplifyApi } from '../../hooks/amplifyApi';
import types from '../types';

export const clearUserData = createAction(types.clearUserData);
export const clearSaveStatus = createAction(types.clearSaveStatus);

export const updateNotificationsPreference = createAsyncThunk(
  types.updateNotificationsPreference,
  async ({ newSettings, personId }) =>
    await amplifyApi(
      newSettings,
      EndPoints.USER.UPDATE_NOTIFICATIONS_PREFERENCE.ENDPOINT.replace(
        '{personId}',
        personId
      ),
      EndPoints.USER.UPDATE_NOTIFICATIONS_PREFERENCE.METHOD
    )
);

const companiesFilterHandler = companies => {
  if (companies.length > 1) return companiesFilter(companies);
  return companies;
};

export const initUserSession = createAsyncThunk(types.userLogIn, async data => {
  const person = await getPerson(data);

  const userRoles = JSON.parse(data.user_roles);

  let roleName = userRoles[0];

  // if user has multiple roles pick dealership manager
  if (userRoles.length > 1)
    roleName = userRoles.find(r => r === UserRoles.DEALERSHIP_MANAGER);

  const translationKey = Object.keys(UserRoles).find(
    k => UserRoles[k] === roleName
  );

  const userRole = {
    roleName,
    translationKey,
  };

  const companyId = companiesFilterHandler(person.companies)[0].companyId;
  const companies = person.companies;
  const approverRights = currentUserDataFilter(
    companyId,
    person.approverRights
  );
  const departments = currentUserDataFilter(companyId, person.departments);

  return {
    data,
    routes: '/orders',
    userRole,
    person,
    companyId,
    companies,
    userRoles,
    approverRights,
    departments,
  };
});

async function getPerson(data) {
  return await amplifyApi(
    null,
    EndPoints.USER.GET_PERSON.ENDPOINT.replace('{personId}', data.person_id),
    EndPoints.USER.GET_PERSON.METHOD
  );
}

export const setUserCurrentCompanyId = createAction(
  types.updateUserCurrentCompany,
  companyId => {
    return {
      payload: companyId,
    };
  }
);

export const setUserCurrentCompanyStatus = createAction(
  types.updateUserCurrentCompanyStatus
);

export const saveUserProfile = createAsyncThunk(
  types.saveUserProfile,
  async ({ personId, data, isLogIn = false }) => {
    const response = await amplifyApi(
      data,
      EndPoints.USER.SAVE_PROFILE.ENDPOINT.replace('{personId}', personId),
      EndPoints.USER.SAVE_PROFILE.METHOD
    );
    return { isLogIn, data: response };
  }
);

export const openProfileManagerDialog = createAction(
  types.openProfileManagerDialog
);

export const closeProfileManagerDialog = createAction(
  types.closeProfileManagerDialog
);

export const clearSaveUserProfileStatus = createAction(
  types.clearSaveUserProfileStatus
);

export const clearVerificationCodeResent = createAction(
  types.clearVerificationCodeResent
);

export const sendVerificationCode = createAsyncThunk(
  types.sendVerificationCode,
  async ({ personId }) => await sendVerificationCodeCallback(personId)
);

export const resendVerificationCode = createAsyncThunk(
  types.resendVerificationCode,
  async ({ personId }) => await sendVerificationCodeCallback(personId)
);

export const verifyCode = createAsyncThunk(
  types.verifyCode,
  async ({ personId, code }) => {
    return await amplifyApi(
      { code },
      EndPoints.USER.VERIFY_VERIFICATION_CODE.ENDPOINT.replace(
        '{personId}',
        personId
      ),
      EndPoints.USER.VERIFY_VERIFICATION_CODE.METHOD
    );
  }
);

const sendVerificationCodeCallback = async personId => {
  return await amplifyApi(
    null,
    EndPoints.USER.SEND_VERIFICATION_CODE.ENDPOINT.replace(
      '{personId}',
      personId
    ),
    EndPoints.USER.SEND_VERIFICATION_CODE.METHOD
  );
};

export const openVerificationModal = createAction(types.openVerificationModal);

export const getWorkOrderToPdf = createAsyncThunk(
  types.getWorkOrderToPdf,
  async ({ order_id }) => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.VEHICLE_EXPORT_WORK_ORDER.ENDPOINT.replace(
        '{order_id}',
        order_id
      ),
      EndPoints.ORDERS.VEHICLE_EXPORT_WORK_ORDER.TYPE
    );
  }
);
